import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Inbox, ArrowUpRight } from 'lucide-react';
import { resolveProfileImage } from '../utils/profileImage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { useMessages } from '../context/MessagesContext';
import { useAuth } from '../context/AuthContext';
import UserAvatar from './UserAvatar';

/**
 * Header Messages dropdown — mirrors the shape of NotificationsDropdown
 * for visual consistency. Lists the 8 most recent conversations with
 * unread badges and a preview. Clicking a row opens the floating
 * messenger widget (persistent across routes) rather than force-
 * navigating to the full Messages page.
 */
export default function MessagesDropdown() {
  const { user } = useAuth();
  const { conversations, totalUnread, openConversation, loading } = useMessages();

  const recent = useMemo(() => {
    return [...conversations]
      .sort((a, b) => {
        const ta = new Date(a.last_message_at || a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.last_message_at || b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      })
      .slice(0, 8);
  }, [conversations]);

  const titleFor = (c) => {
    if (c.is_group) return c.title || 'Group chat';
    const others = (c.participants || []).filter((p) => p.user_id !== user?.id);
    return others[0]?.name || others[0]?.email || 'Direct message';
  };

  const avatarFor = (c) => {
    if (c.is_group) return c.avatar_url || null;
    const other = (c.participants || []).find((p) => p.user_id !== user?.id);
    return resolveProfileImage(other);
  };

  const unreadFor = (c) => {
    if (typeof c.unread_count_for_user === 'number') return c.unread_count_for_user;
    return c.unread_count?.[user?.id] || 0;
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString();
  };

  if (!user) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-theme-text-secondary hover:text-theme-text hover:bg-theme-hover h-10 px-2.5"
          data-testid="messages-dropdown-trigger"
          aria-label="Messages"
        >
          <MessageSquare className="h-5 w-5" />
          {totalUnread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#002FA7] text-white text-[10px] font-bold leading-none ring-2 ring-theme-secondary"
              data-testid="messages-unread-badge"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[360px] p-0 bg-theme-surface border-theme"
        data-testid="messages-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[#002FA7]" />
            <span className="text-sm font-semibold text-theme-text">Messages</span>
            {totalUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#002FA7] text-white text-[11px] font-bold">
                {totalUnread}
              </span>
            )}
          </div>
          <Link to="/messages" className="text-xs text-theme-muted hover:text-[#002FA7] inline-flex items-center gap-0.5">
            Open <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        {loading && recent.length === 0 && (
          <div className="py-8 flex items-center justify-center text-xs text-theme-muted">Loading…</div>
        )}
        {!loading && recent.length === 0 && (
          <div className="py-10 flex flex-col items-center gap-2 text-theme-muted">
            <Inbox className="h-8 w-8 opacity-40" />
            <span className="text-xs">No conversations yet</span>
          </div>
        )}

        <div className="max-h-[400px] overflow-y-auto" data-testid="messages-dropdown-list">
          {recent.map((c) => {
            const title = titleFor(c);
            const unread = unreadFor(c);
            const preview = c.last_message || '';
            const avatar = avatarFor(c);
            const otherParticipant = (c.participants || []).find((p) => p.user_id !== user?.id);

            return (
              <button
                key={c.id}
                onClick={() => openConversation(c.id)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-theme-hover transition-colors border-b border-theme/50 last:border-b-0"
                data-testid={`messages-dropdown-row-${c.id}`}
              >
                <div className="shrink-0 relative">
                  <UserAvatar
                    user={otherParticipant || { name: title, profile_image: avatar }}
                    className="h-9 w-9"
                  />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#002FA7] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-theme-surface" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${unread > 0 ? 'font-semibold text-theme-text' : 'text-theme-text'}`}>
                      {title}
                    </span>
                    <span className="text-[10px] text-theme-muted shrink-0">{timeAgo(c.last_message_at || c.updated_at)}</span>
                  </div>
                  {preview && (
                    <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'text-theme-text' : 'text-theme-muted'}`}>
                      {preview}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
